import router from '@/router'
import PropTypes from 'ant-design-vue/es/_util/vue-types'

import 'ant-design-vue/es/menu/style'
import Menu from 'ant-design-vue/es/menu'
import 'ant-design-vue/es/icon/style'
import Icon from 'ant-design-vue/es/icon'
import { getMenuItemBadgeCount } from '@/utils/menuBadge'



const {
  Item: MenuItem,
  SubMenu
} = Menu

export const RouteMenuProps = {
  menus: PropTypes.array,
  mode: PropTypes.string.def('inline'),
  collapsed: PropTypes.bool.def(false),
  i18nRender: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]).def(false),
  showHelpIcon: PropTypes.bool.def(true)
}

const httpReg = /(http|https|ftp):\/\/([\w.]+\/?)\S*/

const renderMenuBadge = (h, count) => {
  if (!count || count <= 0) {
    return null
  }
  return (
    <a-badge
      class="ant-pro-sider-menu-badge"
      count={count}
      overflowCount={99}
    />
  )
}

const renderMenu = (h, item, i18nRender, showHelpIcon, badgeCounts) => {
  if (item && !item.hidden) {
    const bool = item.children && !item.hideChildrenInMenu
    return bool ? renderSubMenu(h, item, i18nRender, showHelpIcon, badgeCounts) : renderMenuItem(h, item, i18nRender, showHelpIcon, badgeCounts)
  }
  return null
}

const renderSubMenu = (h, item, i18nRender, showHelpIcon, badgeCounts) => {
  // 父级菜单（如「客户管理」）不展示汇总角标，仅子菜单项正常显示
  return (
    <SubMenu key={item.name} title={
      <span>
        {renderTitle(h, item.meta.title, i18nRender, 0)}
      </span>
    }>
      {!item.hideChildrenInMenu && item.children.map(cd => renderMenu(h, cd, i18nRender, showHelpIcon, badgeCounts))}
    </SubMenu>
  )
}

const renderMenuItem = (h, item, i18nRender, showHelpIcon, badgeCounts) => {
  const meta = Object.assign({}, item.meta)
  const target = meta.target || null
  const hasRemoteUrl = httpReg.test(item.path)
  const CustomTag = target && 'a' || 'router-link'
  const props = { to: { name: item.name } }
  const attrs = (hasRemoteUrl || target) ? { href: item.path, target: target } : {}

  if (item.children && item.hideChildrenInMenu) {
    item.children.forEach(cd => {
      cd.meta = Object.assign(cd.meta || {}, { hidden: true })
    })
  }

  return (
    <MenuItem key={item.name} style={{ position: 'relative' }}>
      <CustomTag {...{ props, attrs }}>
        {renderIcon(h, meta.icon)}
        {renderTitle(h, item.meta.title, i18nRender, getMenuItemBadgeCount(item, badgeCounts))}
      </CustomTag>

      {/* 右侧文档图标；教学视频改到帮助文档页底部播放 */}
      {showHelpIcon && item.hasExplanation == 1 && (
        <div
          class="ant-pro-sider-help-icons"
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            gap: '4px'
          }}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
          }}
        >
          <a-icon
            type="file-text"
            theme="outlined"
            style={{ fontSize: '13px', cursor: 'pointer' }}
            on-click={() => {
              router.push({
                path: '/system/HelpDoc/index?menuId=' + item.menuId
              })
            }}
          />
        </div>
      )}
    </MenuItem>
  )
}

const renderIcon = (h, icon) => {
  if (icon === undefined || icon === 'none' || icon === null) {
    return null
  }
  const props = {}
  typeof (icon) === 'object' ? (props.component = icon) : (props.type = icon)
  return <Icon {...{ props }} />
}

const renderTitle = (h, title, i18nRender, badgeCount = 0) => {
  return (
    <span class="ant-pro-sider-menu-title-wrap">
      <span class="ant-pro-sider-menu-title-text">{i18nRender && i18nRender(title) || title}</span>
      {renderMenuBadge(h, badgeCount)}
    </span>
  )
}

const RouteMenu = {
  name: 'RouteMenu',
  props: RouteMenuProps,
  data() {
    return {
      openKeys: [],
      selectedKeys: [],
      cachedOpenKeys: [],
      cachedSelectedKeys: []
    }
  },
  render(h) {
    const { mode, menus, i18nRender, showHelpIcon, customerMenuBadgeCounts } = this

    const handleOpenChange = (openKeys) => {
      if (mode === 'horizontal') {
        this.openKeys = openKeys
        return
      }
      const latestOpenKey = openKeys.find(key => !this.openKeys.includes(key))
      if (!this.rootSubmenuKeys.includes(latestOpenKey)) {
        this.openKeys = openKeys
      } else {
        this.openKeys = latestOpenKey ? [latestOpenKey] : []
      }
    }

    const dynamicProps = {
      props: {
        mode,
        openKeys: this.openKeys,
        selectedKeys: this.selectedKeys
      },
      on: {
        select: menu => {
          this.$emit('select', menu)
          if (!httpReg.test(menu.key)) {
            this.selectedKeys = menu.selectedKeys
          }
        },
        openChange: handleOpenChange
      }
    }

    const menuItems = menus.map(item => {
      if (item.hidden) return null
      return renderMenu(h, item, i18nRender, showHelpIcon, customerMenuBadgeCounts)
    })

    return <Menu {...dynamicProps}>{menuItems}</Menu>
  },
  computed: {
    rootSubmenuKeys() {
      return this.menus.map(item => item.name)
    },
    customerMenuBadgeCounts() {
      return this.$store.getters.customerMenuBadgeCounts || {}
    }
  },
  methods: {
    getMenuRouteNames(menuList = []) {
      const names = []
      menuList.forEach(item => {
        if (item.hidden) {
          return
        }
        if (item.name) {
          names.push(item.name)
        }
        if (item.children && item.children.length) {
          names.push(...this.getMenuRouteNames(item.children))
        }
      })
      return names
    },
    updateMenu() {
      const names = this.$route.meta.names || []
      const selectedName = names[names.length - 1]
      const menuRouteNames = this.getMenuRouteNames(this.menus)
      // 隐藏详情页不在侧栏菜单中，进入时保持侧栏展开/选中状态不变
      if (!selectedName || !menuRouteNames.includes(selectedName)) {
        return
      }
      this.selectedKeys = [selectedName]
      const openKeys = this.mode === 'inline' ? names.filter(Boolean) : []
      this.collapsed ? (this.cachedOpenKeys = openKeys) : (this.openKeys = openKeys)
    }
  },
  created() {
    this.$watch('$route', this.updateMenu)
    this.$watch('collapsed', val => {
      val ? (this.cachedOpenKeys = this.openKeys) : (this.openKeys = this.cachedOpenKeys)
    })
  },
  mounted() {
    this.updateMenu()
  }
}

export default RouteMenu
